/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, {
	forwardRef,
	memo,
	type RefObject,
	useCallback,
	useDeferredValue,
	useEffect,
	useLayoutEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from 'react';

import { cssMap, jsx } from '@compiled/react';
import debounce from 'lodash/debounce';
import { useMergeRefs } from 'use-callback-ref';

import { fg } from '@atlaskit/platform-feature-flags';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';
import { TAB_GAP } from '@atlassian/navigation-kit-common';
import { TabProvider } from '@atlassian/navigation-kit-ui-tab';

import ResponsiveNav from '../../common/ui/responsive-nav';
import TabWrapper from '../../common/ui/tab-wrapper';

import { MoreMenuContents } from './more-menu-contents';
import { MoreTrigger } from './more-trigger';

export type Props = {
	addTab?: React.ReactNode;
	id?: string;
	label: string;
	onMoreClick?: (context: { overflowingTabCount: number }) => void;
	onMoreContentChanged?: (arg: { overflowingTabIds: string[]; visibleTabIds: string[] }) => void;
	selectedTabId?: string | null;
	tabs: {
		id: string;
		tab: React.ReactNode;
	}[];
};

const calculateOverflowTabs = (
	tabContainer: RefObject<HTMLDivElement>,
	showMoreButton: () => () => void,
	selectedTabIndex: number,
) => {
	const overflowTabs: Set<number> = new Set();

	if (tabContainer.current == null) {
		return overflowTabs;
	}

	// All the tabs fit in the container, no need to calculate overflow.
	if (tabContainer.current.scrollWidth <= tabContainer.current.offsetWidth) {
		return overflowTabs;
	}

	// Show the more button as it takes up space in the tab list. This ensures we
	// calculate the tab container width correctly below.
	const resetMoreButtonStyles = showMoreButton();

	// Calculate the tab container width by subtracting padding from the tab container offset width.
	const tabContainerComputedStyle = window.getComputedStyle(tabContainer.current);
	const tabContainerPadding =
		parseFloat(tabContainerComputedStyle.paddingLeft) +
		parseFloat(tabContainerComputedStyle.paddingRight);
	const tabContainerWidth = tabContainer.current.offsetWidth - tabContainerPadding;

	const tabs = tabContainer.current.children as HTMLCollectionOf<HTMLElement>;
	const selectedTabWidth = tabs[selectedTabIndex]?.offsetWidth || 0;
	const selectedTabGap = selectedTabIndex === -1 ? 0 : TAB_GAP;

	const availableWidth = tabContainerWidth - selectedTabWidth - selectedTabGap;

	let tabsWidth = 0;
	let gapCount = 0;

	for (let i = 0; i < tabs.length; i++) {
		// Skip the selected tab, as it should always be visible. Its width and gap are already accounted for above.
		if (i === selectedTabIndex) {
			continue;
		}

		tabsWidth += tabs[i].offsetWidth;

		if (tabsWidth > availableWidth - TAB_GAP * gapCount) {
			overflowTabs.add(i);
		}

		gapCount++;
	}

	resetMoreButtonStyles();

	return overflowTabs;
};

type OverflowAction =
	| { type: 'SET_MUST_RECALCULATE' }
	| { type: 'SET_OVERFLOW_TABS'; payload: Set<number> };

type OverflowState = {
	mustRecalculateOverflowTabs: boolean;

	/**
	 * Set of tab indices that are currently overflowing and should be displayed in the "More" menu.
	 */
	overflowTabs: Set<number>;

	/**
	 * Set of tab indices that have overflowed at least once in the current or previous rendering cycle.
	 *
	 * This state is used to determine which Tab should be rendered with the `TabContext.hasOverflowed` prop set to `true`.
	 *
	 * Use-Case example:
	 * - Hide Spotlights for a Tab when the window is resized and a tab changes from overflowing to visible.
	 */
	overflowedTabs: Set<number>;
};

const initialOverflowState: OverflowState = {
	mustRecalculateOverflowTabs: false,
	overflowTabs: new Set(),
	overflowedTabs: new Set(),
};

const overflowReducer = (state: OverflowState, action: OverflowAction): OverflowState => {
	switch (action.type) {
		case 'SET_MUST_RECALCULATE':
			return {
				...state,
				mustRecalculateOverflowTabs: true,
				overflowTabs: new Set(),
			};
		case 'SET_OVERFLOW_TABS':
			return {
				...state,
				mustRecalculateOverflowTabs: false,
				overflowTabs: action.payload,
				overflowedTabs: new Set([...state.overflowedTabs, ...action.payload]),
			};
		default:
			return state;
	}
};

const TabList = forwardRef(
	(
		{ addTab, id, label, onMoreClick, onMoreContentChanged = () => {}, selectedTabId, tabs }: Props,
		ref: React.Ref<HTMLDivElement>,
	) => {
		const [isMoreOpen, setIsMoreOpen] = useState(false);
		const navRef = useRef<HTMLElement>(null);
		const internalTabContainerRef = useRef<HTMLDivElement>(null);
		const tabContainerRef = useMergeRefs([ref, internalTabContainerRef]);
		const moreButtonWrapperRef = useRef<HTMLDivElement>(null);
		const [{ mustRecalculateOverflowTabs, overflowTabs, overflowedTabs }, dispatch] = useReducer(
			overflowReducer,
			initialOverflowState,
		);

		const selectedTabIndex = tabs.findIndex(({ id }) => id === selectedTabId);
		const overflowingTabCount = overflowTabs.size;
		const hasOverflowTabs = overflowingTabCount > 0;

		const debouncedCalculateOverflowTabs = useMemo(
			() =>
				debounce(
					() => {
						dispatch({ type: 'SET_MUST_RECALCULATE' });
					},
					300,
					{ leading: true },
				),
			[],
		);

		useLayoutEffect(() => {
			debouncedCalculateOverflowTabs();
		}, [debouncedCalculateOverflowTabs, selectedTabId, tabs]);

		useLayoutEffect(() => {
			if (mustRecalculateOverflowTabs) {
				const showMoreButton = () => {
					if (moreButtonWrapperRef.current != null) {
						moreButtonWrapperRef.current.style.display = 'block';
					}

					return () => {
						if (moreButtonWrapperRef.current != null) {
							moreButtonWrapperRef.current.removeAttribute('style');
						}
					};
				};

				dispatch({
					type: 'SET_OVERFLOW_TABS',
					payload: calculateOverflowTabs(internalTabContainerRef, showMoreButton, selectedTabIndex),
				});
			}
		}, [mustRecalculateOverflowTabs, selectedTabIndex]);

		useLayoutEffect(() => {
			const resizeObserver = new ResizeObserver(debouncedCalculateOverflowTabs);

			if (navRef.current) {
				resizeObserver.observe(navRef.current);
			}

			return () => {
				resizeObserver.disconnect();
			};
		}, [debouncedCalculateOverflowTabs]);

		const onMoreTabClick = useCallback(() => {
			setIsMoreOpen((prev) => !prev);
			if (onMoreClick) {
				onMoreClick({ overflowingTabCount });
			}
		}, [onMoreClick, overflowingTabCount]);

		const { overflowingTabs, overflowingTabIds, visibleTabs, visibleTabIds } = useMemo(() => {
			const visibleTabs: React.ReactNode[] = [];
			const overflowingTabs: React.ReactNode[] = [];
			const overflowingTabIds: string[] = [];
			const visibleTabIds: string[] = [];

			tabs.forEach(({ tab, id }, index) => {
				const tabContent = (
					<TabProvider
						id={id}
						isOverflowing={overflowTabs.has(index)}
						hasOverflowed={overflowedTabs.has(index)}
						isSelected={id === selectedTabId}
						key={id}
					>
						{tab}
					</TabProvider>
				);

				if (overflowTabs.has(index)) {
					overflowingTabs.push(tabContent);
					overflowingTabIds.push(id);
				} else {
					visibleTabs.push(tabContent);
					visibleTabIds.push(id);
				}
			});

			return { overflowingTabs, overflowingTabIds, visibleTabs, visibleTabIds };
		}, [overflowedTabs, overflowTabs, selectedTabId, tabs]);

		// overflowingTabIds flickers between empty and non-empty array
		// therefore using useDeferredValue to un-flicker the value as well as JSON.stringify
		// to ensure the effect is triggered based on the value of overflowingTabIds and not the reference.
		const overflowingTabIdsStr = useDeferredValue(JSON.stringify(overflowingTabIds));
		useEffect(() => {
			onMoreContentChanged({ overflowingTabIds, visibleTabIds });
		}, [overflowingTabIdsStr]); // eslint-disable-line react-hooks/exhaustive-deps

		const closeMore = useCallback(() => setIsMoreOpen(false), []);

		const renderPopupContent = useCallback(() => {
			return (
				<MoreMenuContents close={closeMore} selectedTabId={selectedTabId}>
					{overflowingTabs}
				</MoreMenuContents>
			);
		}, [closeMore, overflowingTabs, selectedTabId]);

		const renderPopupTrigger = useCallback(
			(triggerProps: TriggerProps) => (
				<div
					css={[styles.moreButtonWrapper, !hasOverflowTabs && styles.moreButtonWrapperHidden]}
					ref={moreButtonWrapperRef}
				>
					<MoreTrigger
						onClick={onMoreTabClick}
						count={overflowingTabCount}
						{...triggerProps}
						isOpen={isMoreOpen}
					/>
				</div>
			),
			[hasOverflowTabs, isMoreOpen, onMoreTabClick, overflowingTabCount],
		);

		return (
			<ResponsiveNav id={id} ref={navRef} label={label}>
				<TabWrapper ref={tabContainerRef}>{visibleTabs}</TabWrapper>
				<Popup
					content={renderPopupContent}
					isOpen={isMoreOpen}
					onClose={closeMore}
					placement="bottom-end"
					testId="navigation-kit-ui-tab-list.ui.more-popup"
					trigger={renderPopupTrigger}
					shouldRenderToParent={fg('should-render-to-parent-should-be-true-nav')}
				/>
				{addTab && <div css={[styles.addTabWrapper]}>{addTab}</div>}
			</ResponsiveNav>
		);
	},
);

const styles = cssMap({
	addTabWrapper: {
		display: 'flex',
		flex: 'none',
		paddingBlock: token('space.050'),
	},
	moreButtonWrapper: {
		flex: 'none',
	},
	moreButtonWrapperHidden: {
		display: 'none',
	},
});

export default memo(TabList);
